import React, { useState, useEffect } from 'react';
import { useAuth } from '../../utils/AuthContext';
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { PremiumFeatureLock } from '../../components/premium/PremiumFeatureLock';
import { Card, Row, Col, Button, ProgressBar, Alert, Spinner } from 'react-bootstrap';
import { MdAttachMoney, MdTrendingUp, MdTrendingDown, MdRefresh } from 'react-icons/md';
import axios from 'axios';



const BudgetOptimizer = () => {
    const { subscription, user } = useAuth();
    const isPremium = subscription === 'premium';
    
    const [loading, setLoading] = useState(true);
    const [budgetData, setBudgetData] = useState(null);
    const [error, setError] = useState(null);

    const BASE_URL = process.env.REACT_APP_API_URL || '';


    useEffect(() => {
        if (isPremium) {
            fetchBudgetData();
        }
    }, [isPremium]);

    const fetchBudgetData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${BASE_URL}/api/insights/spending`);
            setBudgetData(response.data);
            setError(null);
        } catch (err) {
            setError('Failed to fetch budget data. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const BudgetSummaryCard = ({ title, amount, trend, description }) => (
        <Card className="h-100">
            <Card.Body>
                <h5 className="mb-3">{title}</h5>
                <div className="d-flex align-items-center mb-2">
                    <h3 className="mb-0">
                        <MdAttachMoney className="me-1" />{amount.toFixed(2)}
                    </h3>
                    {trend && (
                        <span className={`ms-2 ${trend > 0 ? 'text-danger' : 'text-success'}`}>
                            {trend > 0 ? <MdTrendingUp className="me-1" /> : <MdTrendingDown className="me-1" />}
                            {Math.abs(trend)}%
                        </span>
                    )}
                </div>
                <p className="text-muted small mb-0">{description}</p>
            </Card.Body>
        </Card>
    );

    const CategoryBudgetCard = ({ category, spent, budget, progress }) => (
        <Card className="mb-3">
            <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">{category}</h6>
                    <span>
                        <MdAttachMoney className="me-1" />
                        {spent.toFixed(2)} / ${budget.toFixed(2)}
                    </span>
                </div>
                <ProgressBar 
                    now={progress} 
                    variant={progress > 90 ? "danger" : progress > 75 ? "warning" : "success"}
                />
            </Card.Body>
        </Card>
    );

    const OptimizationTip = ({ tip }) => (
        <Alert variant="info" className="mb-3">
            <Alert.Heading>{tip.title}</Alert.Heading>
            <p className="mb-0">{tip.description}</p>
        </Alert>
    );

    if (!isPremium) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex align-items-center justify-content-between mb-4">
                        <h4 className="main-title mb-0">Budget Optimizer</h4>
                    </div>

                    <PremiumFeatureLock 
                        title="AI Budget Optimization"
                        description="Let our AI analyze your spending patterns and optimize your budget allocations. Upgrade to premium to access this feature."
                    />
                </div>
                <Footer />
            </React.Fragment>
        );
    }

    if (loading) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4 d-flex justify-content-center align-items-center">
                    <div className="text-center">
                        <Spinner animation="border" role="status" variant="primary" />
                        <p className="mt-2">Analyzing your budget data...</p>
                    </div>
                </div>
                <Footer />
            </React.Fragment>
        );
    }

    if (error) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger">
                        <Alert.Heading>Error</Alert.Heading>
                        <p className="mb-0">{error}</p>
                    </Alert>
                </div>
                <Footer />
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <h4 className="main-title mb-0">Budget Optimizer</h4>
                    <Button 
                        variant="primary" 
                        onClick={fetchBudgetData}
                        className="d-flex align-items-center"
                    >
                        <MdRefresh className="me-2" />
                        Refresh Analysis
                    </Button>
                </div>

                {budgetData && (
                    <>
                        {/* Summary Cards */}
                        <Row className="mb-4 g-3">
                            <Col xs={12} sm={12} lg={4}>
                                <BudgetSummaryCard
                                    title="Monthly Spending"
                                    amount={budgetData.totalSpending || 0}
                                    trend={budgetData.spendingTrend}
                                    description="Total spending this month"
                                />
                            </Col>
                            <Col xs={12} sm={12} lg={4}>
                                <BudgetSummaryCard
                                    title="Potential Savings"
                                    amount={budgetData.potentialSavings || 0}
                                    description="Estimated monthly savings with optimizations"
                                />
                            </Col>
                            <Col xs={12} sm={12} lg={4}>
                                <BudgetSummaryCard
                                    title="Budget Health"
                                    amount={budgetData.budgetHealth || 0}
                                    description="Overall budget optimization score"
                                />
                            </Col>
                        </Row>

                        {/* Category Budgets */}
                        <Card className="mb-4">
                            <Card.Header>
                                <h5 className="mb-0">Category Breakdown</h5>
                            </Card.Header>
                            <Card.Body>
                                {budgetData.categories?.map((category, index) => (
                                    <CategoryBudgetCard
                                        key={index}
                                        category={category.name}
                                        spent={category.spent}
                                        budget={category.budget}
                                        progress={(category.spent / category.budget) * 100}
                                    />
                                ))}
                            </Card.Body>
                        </Card>

                        {/* Optimization Tips */}
                        <Card>
                            <Card.Header>
                                <h5 className="mb-0">Optimization Recommendations</h5>
                            </Card.Header>
                            <Card.Body>
                                {budgetData.recommendations?.map((tip, index) => (
                                    <OptimizationTip key={index} tip={tip} />
                                ))}
                            </Card.Body>
                        </Card>
                    </>
                )}
            </div>
            <Footer />
        </React.Fragment>
    );
};

export default BudgetOptimizer; 